import React from "react";
import { useHistory } from "react-router-dom";
import { Box, Breadcrumbs, Button, CircularProgress, Link, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { scorePercent } from "../AuditHub/utils";
import { AH_BORDER, AH_MUTED, AH_TEXT, backButtonSx } from "../AuditHub/theme";
import { frameworkMatches } from "./ControlAuditPanel";

const scoreLabel = (value) => {
  const percent = scorePercent(value);
  if (percent == null) return "Not yet scored";
  return `${Math.round(percent)}%`;
};

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

/** Account option state for the current framework: the view needs that account's scope and a finished run. */
const accountAvailability = (account, compliance) => {
  const scope = (account.scopes || []).find((item) => frameworkMatches(compliance, item.framework));
  if (!scope) return { scope: null, reason: "Framework not assessed on this account" };
  if (!scope.latestRunId) return { scope, reason: "No completed scan yet" };
  return { scope, reason: "" };
};

/** One row: breadcrumb, audit name and summary, and Back - in the Audit Hub style. */
const AuditContextBar = ({ auditId, state }) => {
  const history = useHistory();

  if (!auditId) return null;

  const audit = state?.audit;
  const accounts = Array.isArray(audit?.accounts) ? audit.accounts : [];
  const accountCount = audit?.accountCount ?? accounts.length;
  const loading = state?.status === "loading" || state?.status === "idle";
  const goToAuditHub = () => history.push("/audit-hub");

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
      <Box sx={{ display: "flex", alignItems: "center", columnGap: 1.5, rowGap: 0.5, flexWrap: "wrap", minWidth: 0, flex: "1 1 320px" }}>
        <Breadcrumbs sx={{ fontSize: 13, color: AH_MUTED, flexShrink: 0 }}>
          <Link component="button" underline="hover" color="inherit" onClick={goToAuditHub} sx={{ fontSize: 13 }}>
            Assignments
          </Link>
          <Typography sx={{ fontSize: 13, color: AH_TEXT }}>{audit?.auditCode || auditId}</Typography>
        </Breadcrumbs>
        <Box aria-hidden sx={{ width: "1px", height: 18, bgcolor: AH_BORDER, display: { xs: "none", sm: "block" } }} />
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={14} />
            <Typography sx={{ fontSize: 13, color: AH_MUTED }}>Loading assignment…</Typography>
          </Box>
        ) : state?.status === "error" ? (
          <Typography sx={{ fontSize: 13, color: "#b91c1c" }}>{state.error}</Typography>
        ) : (
          <Box sx={{ display: "flex", alignItems: "baseline", columnGap: 1.25, rowGap: 0.25, flexWrap: "wrap", minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: AH_TEXT, letterSpacing: "-0.01em", overflowWrap: "anywhere" }}>
              {audit?.name || "Assignment"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: AH_MUTED }}>
              {audit?.ownerEmail ? `Owner ${audit.ownerEmail}` : "No owner"}
              {` · Score ${scoreLabel(audit?.auditScore)}`}
              {` · ${plural(accountCount, "account")}`}
              {` · ${plural(audit?.scopeCount ?? 0, "framework")}`}
            </Typography>
          </Box>
        )}
      </Box>
      <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={goToAuditHub} sx={backButtonSx}>
        Back to Audit Hub
      </Button>
    </Box>
  );
};

export { accountAvailability };
export default AuditContextBar;
